import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { useRecoilState } from "recoil";
import { gqlUserRecoilVar } from "State/User";
import Container from "components/Container/Container";
import { currentModuleAccessRecoilVar } from "State/Navigation";
import {
  GET_BATCHES_WITH_FILTER,
  GET_SLOTS_BY_BATCH_ID,
} from "GraphQl/Queries/BatchesQueries";
import { CREATE_COURSE_BATCHES_MUTATION } from "GraphQl/Mutations/Courses";
import { CREATE_COURSE_BATCHES_SLOTS_MUTATION } from "GraphQl/Mutations/BatchesSlots";
import { errorHandler, successHandler } from "utils/toast";
import React, { useMemo } from "react";
import BatchesData from "./components/BatchesData";
import { Form } from "skillstrainer-resource-library";
import FilterFormBuilder from "formBuilders/FilterFormBuilder";

export default function ListBatches(props) {
  const [user] = useRecoilState(gqlUserRecoilVar);
  const [currentModuleAccess] = useRecoilState(currentModuleAccessRecoilVar);
  const allowedPartnerIds = useMemo(
    () => Object.keys(currentModuleAccess?.allowedPartners || {}),
    [currentModuleAccess],
  );

  const partnerSearchParams = {
    where: { partner_id: { _in: allowedPartnerIds } },
  };

  const batchesDataQuery = useQuery(GET_BATCHES_WITH_FILTER, {
    variables: user.is_skillstrainer_admin ? {} : partnerSearchParams,
  });

  // GQL: Insert batch (used for copy)
  const [insert_course_batches] = useMutation(CREATE_COURSE_BATCHES_MUTATION, {
    onError: (error) => {
      console.log("errors====", error);
      errorHandler("Error creating copied batch");
    },
  });

  // GQL: Insert batch slots (used for copy)
  const [insert_course_batches_slots] = useMutation(
    CREATE_COURSE_BATCHES_SLOTS_MUTATION,
    {
      onError: (error) => {
        console.log("slot copy error====", error);
      },
    },
  );

  // GQL: Fetch slots of the batch being copied
  const [fetchBatchSlots] = useLazyQuery(GET_SLOTS_BY_BATCH_ID, {
    fetchPolicy: "network-only",
  });

  function resetFilters() {
    batchesDataQuery.refetch(
      user.is_skillstrainer_admin ? { where: {} } : partnerSearchParams,
    );
  }

  function filterSearch(filter) {
    const { id, language_id, course, partner_id, name, from_date, to_date } =
      filter;

    let variables = user.is_skillstrainer_admin
      ? { where: {} }
      : partnerSearchParams;

    if (partner_id) {
      variables.where = {
        ...variables.where,
        partner_id: {
          _eq: partner_id,
        },
      };
    }
    if (id) {
      variables.where = {
        ...variables.where,
        id: {
          _eq: parseInt(id),
        },
      };
    }
    if (language_id) {
      variables.where = {
        ...variables.where,
        language_id: {
          _eq: parseInt(language_id),
        },
      };
    }

    if (course) {
      variables.where = {
        ...variables.where,

        course_id: {
          _eq: course,
        },
      };
    }

    if (name) {
      variables.where = {
        ...variables.where,
        batch_name: {
          _ilike: `%${name}%`,
        },
      };
    }
    if (from_date) {
      const d = new Date(from_date);
      d.setUTCHours(0, 0, 0, 0);

      variables.where = {
        ...variables.where,
        from_date: { _gte: d.toISOString() },
      };
    }

    if (to_date) {
      const d = new Date(to_date);
      d.setUTCHours(23, 59, 59, 999);

      variables.where = {
        ...variables.where,
        to_date: { _lte: d.toISOString() },
      };
    }

    batchesDataQuery.refetch(variables);
  }
  // Copy Batch
  const copyBatchFun = async (row) => {
    if (!window.confirm("Do you really want to copy this Batch?")) return;

    try {
      // Build the new batch payload from the existing row.
      // Strip computed/relation/aggregate fields that aren't real columns.
      const {
        id,
        __typename,
        userEnrollmentBatchArray_aggregate,
        course,
        instructor,
        ...rest
      } = row;

      const batchData = {
        ...rest,
        batch_name: row.batch_name + "--copy",
        course_id: row.course?.id ?? row.course_id,
        instructor_id: row.instructor?.id ?? row.instructor_id,
      };

      const { data: insertData } = await insert_course_batches({
        variables: batchData,
      });

      const newBatch = insertData?.insert_courses_course_batches_one;

      if (!newBatch) {
        errorHandler("Something went wrong while copying the batch!");
        return;
      }

      successHandler("Batch Copied, processing slots...");

      // Fetch original batch's slots and clone them onto the new batch
      const { data: slotsData } = await fetchBatchSlots({
        variables: { batch_id: row.id },
      });

      const originalSlots = slotsData?.courses_batch_slots || [];

      originalSlots.forEach((slot) => {
        insert_course_batches_slots({
          variables: {
            batch_id: newBatch.id,
            slot_date: slot.slot_date,
            endto_date: slot.endto_date,
            platform: slot.platform,
            slots_days: slot.slots_days,
            instructor_id: slot.instructor_id,
            type: slot.type,
            location: slot.location,
            // event_id intentionally not copied — it refers to an
            // external calendar event tied to the original batch
          },
        });
      });

      // Refresh the batch list
      batchesDataQuery.refetch();
    } catch (err) {
      console.log(err);
      errorHandler("An error occurred while copying the batch");
    }
  };
  return (
    <Container title="List Batches">
      <Form
        formBuilder={FilterFormBuilder}
        className="mt-2 grid grid-cols-3 gap-x-4 mx-2 w-full"
        submitButton={{
          text: "Filter",
          className:
            "btn-primary bg-orange text-sm font-semibold text-white rounded-md px-6",
        }}
        onSubmit={(filter) => filterSearch(filter)}
      />
      <div className="flex flex-row space-x-2  ml-24 -mt-9">
        <button onClick={resetFilters} className="btn-secondary ">
          <span className="whitespace-nowrap"> Reset Filters</span>
        </button>
      </div>
      <div className="container w-full mt-4">
        <BatchesData
          batchesData={batchesDataQuery}
          copyBatchFun={copyBatchFun}
        />
      </div>
    </Container>
  );
}
